import { FC, useEffect, useState } from 'react';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  listingSuburb?: string;
}

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  listingSuburb,
}) => {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimatingOut(false);
    } else if (shouldRender) {
      setIsAnimatingOut(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match this with CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    if (!isDeleting) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        onClose();
      }, 200);
    }
  };

  if (!shouldRender) return null;

  return (
    <div 
      className={`fixed inset-0 z-[80] flex items-center justify-center ${
        isAnimatingOut ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-200`}
    >
      <div 
        className={`fixed inset-0 bg-gray-500 bg-opacity-75 ${
          isAnimatingOut ? 'opacity-0' : 'opacity-100'
        } transition-opacity duration-200`}
        aria-hidden="true"
        onClick={handleClose}
      />

      <div
        className={`bg-white rounded-xl text-left overflow-hidden shadow-xl sm:max-w-lg w-full relative z-[90] mx-4 sm:mx-0 transform ${
          isAnimatingOut ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        } transition-all duration-300 ease-in-out`}
      >
        <div className="p-6">
          <div className="mb-5">
            <h3 className="text-lg font-medium text-gray-900">Delete Listing</h3>
            <p className="text-sm text-gray-500 mt-1">
              {listingSuburb 
                ? `Are you sure you want to delete your ${listingSuburb} property listing?` 
                : "Are you sure you want to delete this listing?"}
            </p>
          </div>
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent text-white rounded-full mr-2 animate-spin" role="status" aria-label="loading"></span>
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;